import type { Vehicle } from '@/types/vehicle';

const RAKB_TITLE_SUFFIX = /\s*\|\s*RAKB(\.ma)?\s*$/i;

/** SERP-safe caps (Phase 3 on-page SEO). */
export const SEO_TITLE_MAX_CHARS = 60;
export const SEO_DESCRIPTION_MAX_CHARS = 160;

/**
 * Prix journalier minimal sur une liste déjà filtrée (annonces publiques).
 */
export const computeMinListingPricePerDay = (
  vehicles: Vehicle[],
  filters?: { brandMake?: string; categoryDb?: string }
): number | null => {
  let list = vehicles;
  if (filters?.brandMake) {
    const b = filters.brandMake.trim().toLowerCase();
    list = list.filter(v => {
      const make = (v.make || '').toLowerCase();
      const brand = (v.brand || '').toLowerCase();
      return make.includes(b) || brand.includes(b) || make.startsWith(b);
    });
  }
  if (filters?.categoryDb) {
    list = list.filter(v => v.category === filters.categoryDb);
  }
  const prices = list
    .map(v => v.price_per_day ?? v.price ?? 0)
    .filter((p): p is number => typeof p === 'number' && Number.isFinite(p) && p > 0);
  if (prices.length === 0) {
    return null;
  }
  return Math.min(...prices);
};

export type DynamicListingSeoLang = 'fr' | 'en' | 'ar';

export interface MergeDynamicListingSeoOptions {
  /** Nombre d’annonces catalogue « sans caution » (filtre no deposit), pour formulations vérifiables. */
  noDepositVisibleCount?: number | null;
}

const insertTitleSnippet = (title: string, snippet: string): string => {
  if (RAKB_TITLE_SUFFIX.test(title)) {
    return title.replace(RAKB_TITLE_SUFFIX, ` ${snippet} | RAKB`);
  }
  return `${title} ${snippet}`;
};

const softClip = (text: string, max: number): string => {
  const t = text.trim();
  if (t.length <= max) return t;
  const clipped = t.slice(0, Math.max(0, max - 1));
  const lastSpace = clipped.lastIndexOf(' ');
  const body = (lastSpace > Math.floor(max * 0.45) ? clipped.slice(0, lastSpace) : clipped).trimEnd();
  return body ? `${body}…` : t.slice(0, max);
};

/**
 * Enrichit titre + meta description avec un plancher **réel** issu des annonces affichées.
 * FR/EN uniquement pour le prix ; `ar` → pas de suffixe prix (évite incohérence i18n).
 * Title price snippet only if result ≤60 chars. Description ≤160, dynamic suffixes preserved.
 */
export const mergeDynamicListingSeo = (
  baseTitle: string,
  baseDescription: string,
  minPriceMadPerDay: number | null,
  lang: DynamicListingSeoLang,
  options?: MergeDynamicListingSeoOptions
): { title: string; description: string } => {
  let title = baseTitle;
  const extras: string[] = [];

  if (lang !== 'ar' && minPriceMadPerDay !== null && minPriceMadPerDay > 0) {
    const n = Math.round(minPriceMadPerDay);
    if (lang === 'en') {
      const snippet = `— from ${n} MAD/day`;
      const candidate = insertTitleSnippet(baseTitle, snippet);
      if (candidate.length <= SEO_TITLE_MAX_CHARS) {
        title = candidate;
      }
      extras.push(` Lowest per-day rate on current listings from ${n} MAD.`);
    } else {
      const snippet = `— dès ${n} DH/j`;
      const candidate = insertTitleSnippet(baseTitle, snippet);
      if (candidate.length <= SEO_TITLE_MAX_CHARS) {
        title = candidate;
      }
      extras.push(` Tarif/j le plus bas listé : dès ${n} DH.`);
    }
  }

  const nd = options?.noDepositVisibleCount;
  if ((lang === 'fr' || lang === 'en') && typeof nd === 'number' && nd > 0) {
    extras.push(
      lang === 'en'
        ? ` ${nd} listings show no deposit hold.`
        : ` ${nd} annonces sans caution listée.`
    );
  }

  const suffix = extras.join('');
  const room = Math.max(48, SEO_DESCRIPTION_MAX_CHARS - suffix.length);
  const description = `${softClip(baseDescription, room)}${suffix}`.trim();

  return {
    title,
    description: softClip(description, SEO_DESCRIPTION_MAX_CHARS),
  };
};
